import { EXPORT_MAX_ROWS, FINANCING_LABELS, type DealDto } from '@performance-crm/shared';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListFooter } from '@/components/commercial/ListFooter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { fmt, todayInTz } from '@/lib/format';
import { fetchAllPages, useAdvisors, useDeals, type DealListParams } from '@/lib/queries';
import { downloadWorkbook } from '@/lib/spreadsheet';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { useDebounced } from '@/lib/useDebounced';
import { dealsSheet } from './commercialExport';

const STATE: Record<string, { tone: 'success' | 'warning' | 'danger'; label: string }> = {
  ACTIVO: { tone: 'success', label: 'Activa' },
  DEVUELTO: { tone: 'warning', label: 'Devuelta' },
  ANULADO: { tone: 'danger', label: 'Anulada' },
};

/** Qué fecha filtra el rango según el tipo elegido (sin tipo, cada proceso usa la suya). */
const DATE_HINT: Record<string, string> = {
  SEPARACION: 'Las fechas filtran por la fecha de separación',
  VENTA: 'Las fechas filtran por la fecha de venta',
  '': 'Las fechas filtran por la fecha de cada proceso: separación o venta según su tipo',
};

/**
 * Separaciones y ventas: filtros por tipo, estado, fechas (días de la empresa), asesor y cliente, «Cargar más»,
 * Excel y columnas separadas para la reserva (separaciones) y el precio total (ventas).
 */
export function DealsTab({ companyId, projectId }: { companyId: string; projectId?: string }) {
  const tz = useCompanyTimezone(companyId);
  const [type, setType] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [advisorId, setAdvisorId] = useState('');
  const [exporting, setExporting] = useState(false);
  const toast = useToast();
  const { data: advisors } = useAdvisors(companyId);
  const needle = useDebounced(q.trim(), 300);
  const params: DealListParams = {
    companyId,
    projectId,
    type: type || undefined,
    state: state || undefined,
    q: needle.length >= 2 ? needle : undefined,
    from: from || undefined,
    to: to || undefined,
    advisorId: advisorId || undefined,
  };
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useDeals(params);
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total;
  const exportExcel = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPages<DealDto>('/api/v1/deals', params);
      await downloadWorkbook(
        [dealsSheet(all.items, tz)],
        `procesos-${from || 'inicio'}_a_${to || todayInTz(tz)}.xlsx`,
      );
      if (all.truncated)
        toast.error(
          'Excel incompleto',
          `Se exportaron los primeros ${fmt.number(EXPORT_MAX_ROWS)} procesos: acota las fechas para el resto.`,
        );
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cliente…"
          aria-label="Buscar proceso"
          className="h-9 w-56"
        />
        <Select
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Tipo de proceso"
          className="h-9 w-44"
        >
          <option value="">Separaciones y ventas</option>
          <option value="SEPARACION">Separaciones</option>
          <option value="VENTA">Ventas</option>
        </Select>
        <Select
          value={state}
          onChange={(e) => setState(e.target.value)}
          aria-label="Estado del proceso"
          className="h-9 w-44"
        >
          <option value="">Todos los estados</option>
          <option value="ACTIVO">Activas</option>
          <option value="DEVUELTO">Devueltas</option>
          <option value="ANULADO">Anuladas</option>
        </Select>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Desde"
          className="h-9 w-40"
        />
        <span className="text-muted-foreground text-sm">a</span>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Hasta"
          className="h-9 w-40"
        />
        <Select
          value={advisorId}
          onChange={(e) => setAdvisorId(e.target.value)}
          aria-label="Asesor"
          className="h-9 w-52"
        >
          <option value="">Todos los asesores</option>
          {(advisors ?? [])
            .filter((a) => a.kind === 'HUMAN')
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {data && (
            <p className="text-muted-foreground text-sm" data-testid="deals-total">
              {fmt.number(total ?? items.length)} procesos
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void exportExcel()}
            disabled={!data || items.length === 0 || exporting}
            data-testid="export-deals"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}{' '}
            Exportar Excel
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground text-xs" data-testid="deals-date-hint">
        {DATE_HINT[type]} (días completos en la zona de la empresa). <b>Reserva</b> es el monto de
        la separación; <b>Precio</b>, el total de la venta.
      </p>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Proyecto</th>
                <th className="px-3 py-2">Unidades</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-2 py-2 text-right">Reserva</th>
                <th className="px-2 py-2 text-right">Precio</th>
                <th className="px-3 py-2">Modalidad</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Asesor</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t" data-testid="deal-list-row">
                  <td className="px-3 py-2 font-medium">
                    {d.type === 'VENTA' ? 'Venta' : 'Separación'}
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/leads/${d.leadId}`} className="text-primary hover:underline">
                      {d.contactName}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{d.projectName}</td>
                  <td className="px-3 py-2">{d.units.map((u) => u.code).join(', ') || '—'}</td>
                  <td className="px-3 py-2">
                    {fmt.date(d.type === 'VENTA' ? d.finishedAt : d.startedAt)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {d.type === 'SEPARACION' ? fmt.money(d.amountInitial, d.initialCurrency) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {d.type === 'VENTA' ? fmt.money(d.amountTotal, d.currency) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {d.contractMode ? FINANCING_LABELS[d.contractMode] : '—'}
                    {d.bank ? ` · ${d.bank}` : ''}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATE[d.state]!.tone}>{STATE[d.state]!.label}</Badge>
                    {d.state === 'DEVUELTO' && (
                      <p className="text-muted-foreground text-xs">
                        {fmt.date(d.refundedAt)} · {d.dropReason}
                      </p>
                    )}
                    {d.state === 'ANULADO' && (
                      <p className="text-muted-foreground text-xs">{d.annulReason}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">{d.advisorName ?? '—'}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-muted-foreground px-3 py-8 text-center">
                    Sin procesos con estos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <ListFooter
        shown={items.length}
        total={total}
        noun="procesos"
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onMore={() => void fetchNextPage()}
        testId="deals-footer"
      />
    </div>
  );
}
